import { Injectable } from '@angular/core';

/**
 * Public-email-provider detection. Moved into the shared library because BOTH portals gate the
 * company upgrade on it: an account registered with a free-mail address cannot claim a company
 * domain, and both apps must answer that question identically.
 *
 * The list is data, not logic — keep additions alphabetically near their provider group.
 */
@Injectable({
  providedIn: 'root',
})
export class EmailValidatorService {
  /**
   * List of common public email providers to prevent company upgrades
   */
  private readonly publicEmailDomains: string[] = [
    // Google
    'gmail.com',
    'googlemail.com',
    'googlemail.de',
    'googlemail.co.uk',
    // Microsoft
    'hotmail.com',
    'hotmail.de',
    'hotmail.fr',
    'hotmail.co.uk',
    'outlook.com',
    'outlook.de',
    'outlook.fr',
    'outlook.co.uk',
    'outlook.jp',
    'live.com',
    'live.de',
    'live.fr',
    'msn.com',
    'passport.com',
    // Yahoo (inkl. internationaler Varianten)
    'yahoo.com',
    'ymail.com',
    'rocketmail.com',
    'yahoo.co.uk',
    'yahoo.fr',
    'yahoo.de',
    'yahoo.it',
    'yahoo.es',
    'yahoo.com.ar',
    'yahoo.com.br',
    'yahoo.com.mx',
    'yahoo.co.jp',
    'yahoo.co.kr',
    'yahoo.co.nz',
    'yahoo.com.au',
    'yahoo.ca',
    'yahoo.co.in',
    // AOL
    'aol.com',
    'aim.com',
    // Apple
    'icloud.com',
    'me.com',
    'mac.com',
    // Weitere große Anbieter
    'gmx.com',
    'gmx.net',
    'gmx.de',
    'gmx.at',
    'gmx.ch',
    'gmx.us',
    'mail.com',
    'mail.ru',
    'web.de',
    'comcast.net',
    'verizon.net',
    'att.net',
    'protonmail.com',
    'protonmail.ch',
    'pm.me',
    'proton.me',
    'zoho.com',
    'yandex.ru',
    'yandex.com',
    'tutanota.com',
    'tutanota.de',
    'fastmail.com',
    'fastmail.fm',
    't-online.de',
    'freenet.de',
    // Premium / Privacy-fokussiert
    'hushmail.com',
    'hey.com',
    'mailbox.org',
    'posteo.de',
    'kolabnow.com',
    'mailfence.com',
    'rediffmail.com',
    'lycos.com',
    'runbox.com',
    'startmail.com',
    'ctemplar.com',
    'countermail.com',
    // Community-/FOSS-Anbieter
    'openmailbox.org',
    'riseup.net',
    'disroot.org',
    'autistici.org',
    'teknik.io',
    'firemail.cc',
    'cock.li',
    'waifu.club',
    '420blaze.it',
    'redd.it',
    'memeware.net',
    'airmail.cc',
    'spymail.one',
    // Temporäre Maildienste
    'mailinator.com',
    'guerrillamail.com',
    'temp-mail.org',
    '10minutemail.com',
    'yopmail.com',
    'dispostable.com',
    'trashmail.com',
    'maildrop.cc',
    'getnada.com',
    'nada.email',
    // Regionale/ISP-Provider USA
    'cox.net',
    'charter.net',
    'bellsouth.net',
    'sbcglobal.net',
    'earthlink.net',
    'juno.com',
    'peoplepc.com',
    'netscape.net',
    'inbox.com',
    'myway.com',
    'excite.com',
    // Europa (außer DE/FR/UK already oben)
    'virgilio.it',
    'terra.es',
    'telefonica.net',
    'wanadoo.fr',
    'sfr.fr',
    'neuf.fr',
    'club-internet.fr',
    'dbmail.com',
    'caramail.com',
    'libertysurf.fr',
    'proximus.be',
    'xs4all.nl',
    'ziggo.nl',
    'planet.nl',
    'skynet.be',
    'telenet.be',
    'btconnect.com',
    'btopenworld.com',
    'btinternet.com',
    'talktalk.net',
    'sky.com',
    'virginmedia.com',
    // Deutschland
    'arcor.de',
    'kabelmail.de',
    'kabelbw.de',
    'online.de',
    'ewe.net',
    'ish.de',
    'unitybox.de',
    'ok.de',
    // Italien
    'libero.it',
    'tiscali.it',
    'alice.it',
    'tin.it',
    'poste.it',
    'inwind.it',
    'iol.it',
    'supereva.it',
    // Polen
    'wp.pl',
    'o2.pl',
    'interia.pl',
    'gazeta.pl',
    'onet.pl',
    // Russland
    'rambler.ru',
    'inbox.ru',
    'bk.ru',
    'list.ru',
    'ro.ru',
    // Ukraine
    'ukr.net',
    'meta.ua',
    'i.ua',
    'bigmir.net',
    // Tschechien/Slowakei
    'centrum.cz',
    'seznam.cz',
    'atlas.cz',
    'azet.sk',
    'zoznam.sk',
    'centrum.sk',
    // Spanien/Portugal
    'sapo.pt',
    'portugalmail.pt',
    'hispavista.com',
    'mixmail.com',
    'netcabo.pt',
    // Ungarn
    'freemail.hu',
    'citromail.hu',
    't-email.hu',
    // Bulgarien
    'abv.bg',
    'mail.bg',
    'dir.bg',
    // Asien
    'qq.com',
    '163.com',
    '126.com',
    '188.com',
    'yeah.net',
    '21cn.com',
    'sina.com',
    'sina.cn',
    'tom.com',
    'foxmail.com',
    'sohu.com',
    'aliyun.com',
    'naver.com',
    'daum.net',
    'hanmail.net',
    'nate.com',
    // Japan
    'biglobe.ne.jp',
    'nifty.com',
    'goo.ne.jp',
    'docomo.ne.jp',
    // Weitere internationale Domains
    'kimo.com', // Taiwan
    'walla.co.il', // Israel
    'walla.com', // Israel
    'zahav.net.il', // Israel
    'netvision.net.il', // Israel
    'tut.by', // Belarus
    'mail.ee', // Estland
    'inbox.lv', // Lettland
    // Indien
    'indiatimes.com',
    'sify.com',
    'in.com',
    'webmail.co.in',
    'nic.in',
    // Südamerika
    'uol.com.br',
    'bol.com.br',
    'globo.com',
    'ig.com.br',
    // Email-Service-Template-Domains
    'email.com',
    'myself.com',
    'netaddress.com',
    'safe-mail.net',
    'gawab.com',
    'engineer.com',
    'accountant.com',
    'techie.com',
    'usa.com',
    'consultant.com',
    'dr.com',
    'cheerful.com',
    'musician.org',
    'russia.com',
    'dublin.com',
    'berlin.com',
    'munich.com',
    'hamburg.com',
    'europe.com',
    'asia.com',
    'tokyo.com',
    'duck.com', // Weiterleitungsdienst von Duck Duck Go
    // Hostelry-Domains
    'one.com',
    'hostinger.com',
    'zohomail.eu',
    // Edu-ähnliche Domains (aber öffentlich)
    'student.com',
    'alumni.com',
    'graduate.org',
    'teachers.org',
    // Business Templates
    'testDomainCypress.com',
    'example.org',
    'example.net',
    'domain.com',
    'domain.net',
    // Test Domains
    'test.com',
    'test.de',
    'test.net',
    'test.org',
  ];

  /**
   * Checks if the given email domain is from a public provider
   * @param domain The email domain to check
   * @returns true if it's a public email provider, false otherwise
   */
  isPublicEmailDomain(domain: string): boolean {
    if (!domain) return false;

    const normalizedDomain = domain.toLowerCase().trim();
    return this.publicEmailDomains.includes(normalizedDomain);
  }

  /**
   * Extracts the domain part from an email address
   * @param email The full email address
   * @returns The domain part or null if invalid
   */
  extractDomainFromEmail(email: string): string | null {
    if (!email || !email.includes('@')) {
      return null;
    }
    return email.split('@')[1].toLowerCase().trim();
  }

  /**
   * Checks if an email is from a public provider
   * @param email The full email address to check
   * @returns true if it's a public email, false otherwise
   */
  isPublicEmail(email: string): boolean {
    const domain = this.extractDomainFromEmail(email);
    if (!domain) return false;

    return this.isPublicEmailDomain(domain);
  }
}
